<template>
 <Teleport to="body">
 <div class="fixed inset-0 bg-black/50 flex items-center justify-center z-50" @click.self="requestClose">
 <div class="bg-surface dark:bg-surface-dark rounded-xl shadow-xl w-full max-w-lg mx-4 border border-stroke dark:border-stroke-dark max-h-[90vh] flex flex-col">

 <!-- Header -->
 <div class="flex items-start justify-between px-5 pt-5 pb-3">
 <div class="min-w-0">
 <h3 class="text-base font-semibold text-fg-base dark:text-fg-base-dark leading-snug">Homework</h3>
 <p class="text-xs text-fg-muted dark:text-fg-muted-dark mt-0.5 truncate">
 {{ lesson.lesson_title }} · {{ formatDate(lesson.lesson_date) }}
 <span v-if="homework?.status === 'sent'" class="ml-1 text-green-700 dark:text-green-400">· Sent {{ formatSent(homework.published_at) }}</span>
 <span v-else-if="homework" class="ml-1 text-amber-700 dark:text-amber-400">· Draft</span>
 </p>
 </div>
 <button @click="requestClose" class="ml-3 mt-0.5 p-1 text-fg-faint dark:text-fg-faint-dark rounded transition-colors flex-shrink-0">
 <XMarkIcon class="h-5 w-5" />
 </button>
 </div>

 <div v-if="loading" class="px-5 pb-6 text-sm text-fg-muted dark:text-fg-muted-dark">Loading…</div>

 <div v-else class="px-5 pb-5 overflow-y-auto space-y-4">

 <div v-if="demoActive" class="text-xs rounded-md px-3 py-2 bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300">
 Homework can't be changed in Demo Mode.
 </div>

 <!-- Note -->
 <div>
 <label class="block text-xs font-medium text-fg-secondary dark:text-fg-secondary-dark mb-1">Note for the family</label>
 <textarea v-model="note" rows="4" maxlength="5000" :disabled="demoActive"
 placeholder="What to practice this week…"
 class="w-full rounded-md border border-stroke dark:border-stroke-dark bg-surface dark:bg-surface-dark text-sm text-fg-base dark:text-fg-base-dark px-3 py-2"
 @blur="saveDraft(true)" />
 </div>

 <!-- Recipients (group lessons) -->
 <div v-if="students.length > 1">
 <div class="text-xs font-medium text-fg-secondary dark:text-fg-secondary-dark mb-1">Send to</div>
 <label v-for="s in students" :key="s.id" class="flex items-center gap-2 text-sm text-fg-base dark:text-fg-base-dark py-0.5">
 <input type="checkbox" :value="s.id" v-model="studentIds" :disabled="demoActive" @change="saveDraft(true)" />
 {{ s.name }}
 </label>
 </div>
 <div v-else-if="!students.length" class="text-xs rounded-md px-3 py-2 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300">
 This lesson has no student linked, so there's no family to send homework to.
 </div>

 <!-- Files -->
 <div>
 <div class="flex items-center justify-between mb-1">
 <span class="text-xs font-medium text-fg-secondary dark:text-fg-secondary-dark">Photos &amp; videos</span>
 <span class="text-xs text-fg-faint dark:text-fg-faint-dark">{{ fileCount }} / {{ limits.max_files }}</span>
 </div>

 <div v-if="files.length || uploads.length" class="grid grid-cols-3 gap-2 mb-2">
 <div v-for="f in files" :key="'f' + f.id"
 class="relative aspect-square rounded-md overflow-hidden border border-stroke dark:border-stroke-dark bg-surface-muted dark:bg-surface-dark flex items-center justify-center">
 <a v-if="f.status === 'ready'" :href="f.url" target="_blank" rel="noopener" class="block w-full h-full">
 <img :src="f.thumb_url" class="w-full h-full object-cover" alt="" />
 <span v-if="f.kind === 'video'" class="absolute inset-0 flex items-center justify-center">
 <span class="rounded-full bg-black/60 text-white text-xs px-2 py-1">▶ {{ formatDuration(f.duration_s) }}</span>
 </span>
 </a>
 <div v-else-if="f.status === 'failed'" class="p-2 text-center text-xs text-red-700 dark:text-red-400">
 Couldn't process<br><span class="text-fg-faint dark:text-fg-faint-dark">{{ f.error }}</span>
 </div>
 <div v-else class="p-2 text-center text-xs text-fg-muted dark:text-fg-muted-dark">
 <ArrowPathIcon class="h-5 w-5 mx-auto mb-1 animate-spin" />
 {{ f.kind === 'video' ? 'Converting video…' : 'Processing…' }}
 </div>
 <button v-if="!demoActive" @click="removeFile(f)" title="Remove"
 class="absolute top-1 right-1 rounded-full bg-black/60 text-white p-0.5">
 <XMarkIcon class="h-4 w-4" />
 </button>
 </div>

 <div v-for="u in uploads" :key="u.key"
 class="relative aspect-square rounded-md border border-stroke dark:border-stroke-dark bg-surface-muted dark:bg-surface-dark flex flex-col items-center justify-center p-2 text-center">
 <div class="text-xs text-fg-secondary dark:text-fg-secondary-dark truncate w-full">{{ u.name }}</div>
 <template v-if="u.error">
 <div class="text-xs text-red-700 dark:text-red-400 mt-1">{{ u.error }}</div>
 <button @click="dismissUpload(u)" class="text-xs underline text-fg-muted dark:text-fg-muted-dark mt-1">Dismiss</button>
 </template>
 <template v-else>
 <div class="w-full h-1.5 rounded bg-stroke dark:bg-stroke-dark mt-2 overflow-hidden">
 <div class="h-full bg-blue-600 transition-all" :style="{ width: u.progress + '%' }"></div>
 </div>
 <div class="text-xs text-fg-muted dark:text-fg-muted-dark mt-1">{{ u.progress < 100 ? `Uploading ${u.progress}%` : 'Finishing…' }}</div>
 </template>
 </div>
 </div>

 <label v-if="!demoActive && fileCount < limits.max_files"
 class="inline-flex items-center gap-1.5 text-sm text-blue-600 dark:text-blue-400 cursor-pointer">
 <PlusIcon class="h-4 w-4" /> Add photos/videos
 <input type="file" accept="image/*,video/*" multiple class="hidden" @change="onPick" />
 </label>
 <p class="text-xs text-fg-faint dark:text-fg-faint-dark mt-1">
 Videos should show you demonstrating — please don't film students.
 Up to {{ limits.max_video_minutes }} min per video.
 </p>
 </div>

 <div v-if="error" class="text-xs rounded-md px-3 py-2 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300">{{ error }}</div>

 <!-- Actions -->
 <div v-if="!demoActive" class="flex items-center justify-between pt-1">
 <button v-if="homework" @click="deleteHomework" class="text-xs text-red-600 dark:text-red-400 underline">Delete homework</button>
 <span v-else></span>
 <div class="flex gap-2">
 <button v-if="homework?.status !== 'sent'" @click="saveDraft(false)" :disabled="busy" class="btn-secondary text-sm">Save draft</button>
 <button @click="send" :disabled="!canSend" class="btn-primary text-sm">
 {{ homework?.status === 'sent' ? 'Update' : 'Send to family' }}
 </button>
 </div>
 </div>
 <p v-if="!demoActive && sendHint" class="text-xs text-fg-faint dark:text-fg-faint-dark text-right -mt-2">{{ sendHint }}</p>
 </div>
 </div>
 </div>
 </Teleport>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { XMarkIcon, PlusIcon, ArrowPathIcon } from '@heroicons/vue/24/outline'
import { useApi } from '../../composables/useApi'
import { useDemoMode } from '../../composables/useDemoMode'

const props = defineProps({
 lesson: { type: Object, required: true }
})
const emit = defineEmits(['close', 'status'])

const { fetchApi } = useApi()
const demoActive = useDemoMode().active

const loading = ref(true)
const busy = ref(false)
const error = ref('')
const homework = ref(null)
const students = ref([])
const limits = ref({ max_files: 10, max_video_minutes: 5, max_image_mb: 25, max_video_mb: 600, types: [] })
const note = ref('')
const studentIds = ref([])
const uploads = ref([]) // in-flight browser uploads: { key, name, progress, error }
let savedNote = ''
let savedIds = ''
let pollTimer = null

const base = `/api/homework/lessons/${props.lesson.id}`
const files = computed(() => homework.value?.files || [])
const fileCount = computed(() => files.value.length + uploads.value.filter(u => !u.error).length)
const pending = computed(() =>
 uploads.value.some(u => !u.error) || files.value.some(f => f.status === 'uploading' || f.status === 'processing'))
const hasContent = computed(() => note.value.trim() || files.value.some(f => f.status === 'ready'))
const canSend = computed(() => !busy.value && !pending.value && hasContent.value && studentIds.value.length > 0)
const sendHint = computed(() => {
 if (pending.value) return 'Waiting for files to finish…'
 if (!hasContent.value) return 'Add a note or a file to send.'
 if (!studentIds.value.length) return 'Choose at least one student.'
 return ''
})

// ── Loading & polling ──────────────────────────────────────────────────────

const apply = (hw) => {
 homework.value = hw
 emit('status', hw ? hw.status : 'none')
 schedulePoll()
}

const load = async () => {
 try {
 const data = await fetchApi(base)
 students.value = data.students || []
 if (data.limits) limits.value = data.limits
 const hw = data.homework
 note.value = savedNote = hw?.note || ''
 studentIds.value = hw ? [...hw.student_ids] : students.value.map(s => s.id)
 savedIds = JSON.stringify(studentIds.value)
 apply(hw)
 } catch (e) {
 error.value = e.detail || 'Could not load homework.'
 } finally {
 loading.value = false
 }
}

const refreshFiles = async () => {
 try {
 const data = await fetchApi(base)
 apply(data.homework)
 } catch { /* next poll retries */ }
}

const schedulePoll = () => {
 clearTimeout(pollTimer)
 if (files.value.some(f => f.status === 'processing' || f.status === 'uploading')) {
 pollTimer = setTimeout(refreshFiles, 4000)
 }
}

onMounted(load)
onBeforeUnmount(() => clearTimeout(pollTimer))

// ── Draft save / send / delete ─────────────────────────────────────────────

const saveDraft = async (quiet) => {
 if (demoActive.value) return
 const ids = JSON.stringify(studentIds.value)
 if (quiet && note.value === savedNote && ids === savedIds) return
 if (quiet && !homework.value && !note.value.trim()) return
 busy.value = !quiet
 try {
 const data = await fetchApi(base, { method: 'PUT', body: JSON.stringify({ note: note.value, student_ids: studentIds.value }) })
 savedNote = note.value
 savedIds = ids
 apply(data.homework)
 if (!quiet) emit('close')
 } catch (e) {
 error.value = e.detail || 'Could not save.'
 } finally {
 busy.value = false
 }
}

const send = async () => {
 busy.value = true
 error.value = ''
 try {
 await fetchApi(base, { method: 'PUT', body: JSON.stringify({ note: note.value, student_ids: studentIds.value }) })
 savedNote = note.value
 savedIds = JSON.stringify(studentIds.value)
 const data = await fetchApi(`${base}/send`, { method: 'POST' })
 apply(data.homework)
 emit('close')
 } catch (e) {
 error.value = e.detail || 'Could not send.'
 } finally {
 busy.value = false
 }
}

const deleteHomework = async () => {
 const msg = homework.value?.status === 'sent'
 ? 'Delete this homework? It will disappear from the family\'s practice page.'
 : 'Delete this draft and its files?'
 if (!confirm(msg)) return
 busy.value = true
 try {
 await fetchApi(base, { method: 'DELETE' })
 apply(null)
 emit('close')
 } catch (e) {
 error.value = e.detail || 'Could not delete.'
 } finally {
 busy.value = false
 }
}

const requestClose = async () => {
 if (uploads.value.some(u => !u.error) && !confirm('Files are still uploading. Close anyway? Unfinished uploads will be lost.')) return
 await saveDraft(true)
 emit('close')
}

// ── Uploads ────────────────────────────────────────────────────────────────

// Some browsers leave file.type empty for .mov/.heic — fall back to the extension.
const EXT_TYPES = { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', heic: 'image/heic', heif: 'image/heif',
 webp: 'image/webp', mp4: 'video/mp4', mov: 'video/quicktime', webm: 'video/webm', m4v: 'video/x-m4v' }
const typeOf = (file) => {
 const ext = (file.name.split('.').pop() || '').toLowerCase()
 return (file.type || EXT_TYPES[ext] || '').toLowerCase()
}

// Reject over-long videos before spending minutes uploading them. Unknown duration
// (e.g. HEVC this browser can't decode) passes — the server checks again.
const videoSeconds = (file) => new Promise(resolve => {
 const v = document.createElement('video')
 const url = URL.createObjectURL(file)
 const done = (val) => { URL.revokeObjectURL(url); resolve(val) }
 const t = setTimeout(() => done(null), 5000)
 v.preload = 'metadata'
 v.onloadedmetadata = () => { clearTimeout(t); done(Number.isFinite(v.duration) ? v.duration : null) }
 v.onerror = () => { clearTimeout(t); done(null) }
 v.src = url
})

const putFile = (url, file, contentType, onProgress) => new Promise((resolve, reject) => {
 const xhr = new XMLHttpRequest() // fetch() can't report upload progress
 xhr.open('PUT', url)
 xhr.setRequestHeader('Content-Type', contentType)
 xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100)) }
 xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed (${xhr.status})`)))
 xhr.onerror = () => reject(new Error('Upload failed — check your connection'))
 xhr.send(file)
})

const uploadOne = async (file) => {
 const u = { key: `${Date.now()}-${Math.random()}`, name: file.name, progress: 0, error: '' }
 uploads.value.push(u)
 const entry = () => uploads.value.find(x => x.key === u.key)
 const fail = (msg) => { const e = entry(); if (e) e.error = msg }
 try {
 const contentType = typeOf(file)
 if (contentType.startsWith('video/')) {
 const secs = await videoSeconds(file)
 if (secs && secs > limits.value.max_video_minutes * 60 + 2) {
 return fail(`Too long (${Math.ceil(secs / 60)} min) — limit is ${limits.value.max_video_minutes} min`)
 }
 }
 const start = await fetchApi(`${base}/files`, {
 method: 'POST', body: JSON.stringify({ filename: file.name, content_type: contentType, size: file.size })
 })
 await putFile(start.put_url, file, start.content_type, (p) => { const e = entry(); if (e) e.progress = p })
 await fetchApi(`/api/homework/files/${start.file_id}/complete`, { method: 'POST' })
 uploads.value = uploads.value.filter(x => x.key !== u.key)
 await refreshFiles()
 } catch (e) {
 fail(e.detail || e.message || 'Upload failed')
 }
}

const onPick = async (ev) => {
 const picked = Array.from(ev.target.files || [])
 ev.target.value = ''
 error.value = ''
 const room = limits.value.max_files - fileCount.value
 if (picked.length > room) error.value = `Only ${room} more file${room === 1 ? '' : 's'} can be added.`
 // One at a time: parallel uploads from a phone just split the same bandwidth.
 for (const file of picked.slice(0, Math.max(room, 0))) await uploadOne(file)
}

const dismissUpload = (u) => { uploads.value = uploads.value.filter(x => x.key !== u.key) }

const removeFile = async (f) => {
 try {
 await fetchApi(`/api/homework/files/${f.id}`, { method: 'DELETE' })
 await refreshFiles()
 } catch (e) {
 error.value = e.detail || 'Could not remove that file.'
 }
}

// ── Formatting ─────────────────────────────────────────────────────────────

const formatDate = (d) => {
 const date = /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(d + 'T00:00:00') : new Date(d)
 return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}
const formatSent = (iso) => iso
 ? new Date(iso).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
 : ''
const formatDuration = (s) => s ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` : ''
</script>
